import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './YoutubeStoryteller.css';
import YoutubeStorytellerStep1 from './YoutubeStorytellerStep1';
import YoutubeStorytellerStep2 from './YoutubeStorytellerStep2';
import YoutubeStorytellerStep3 from './YoutubeStorytellerStep3';
import YoutubeStorytellerStep4 from './YoutubeStorytellerStep4';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';

const YoutubeStoryteller = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);
  const [formData, setFormData] = useState({
    // Step 1 Data
    mainIdeas: '',
    selectedTags: [],
    language: 'german',
    // Step 2 Data (NEW: 3 separate sections)
    newsArticles: [],
    storyBlocks: [],
    narrativeStories: [],
    usage: {},
    // Step 3 Data
    selectedNews: [],
    selectedBlocks: [],
    selectedNarratives: [],
    scriptConfig: {}
  });
  
  // Step 4 Data - Generated Scripts + Title Ideas
  const [generatedScripts, setGeneratedScripts] = useState({
    scriptA: null,
    scriptB: null,
    titleIdeas: [],
    usage: null
  });
  
  const [isGeneratingScripts, setIsGeneratingScripts] = useState(false);

  const handleStep1Complete = (step1Data) => {
    setFormData({ ...formData, ...step1Data });
    setCurrentStep(2);
  };

  const handleStep2Complete = (step2Data) => {
    setFormData({ ...formData, ...step2Data });
    setCurrentStep(3);
  };

  const handleStep3Complete = async (finalPayload) => {
    console.log('🚀 Final Payload for Script Generation:', finalPayload);
    
    setIsGeneratingScripts(true);
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Get ID token for authentication
      const idToken = await user.getIdToken();
      
      // Call Firebase Cloud Function (automatically switches between dev/prod)
      const functionUrl = CLOUD_FUNCTION_URLS.generateYoutubeScript();
      
      console.log('📡 Calling Cloud Function:', functionUrl);
      
      const response = await authenticatedFetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          ...finalPayload,
          userId: user.uid
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate scripts');
      }
      
      const data = await response.json();
      console.log('✅ Scripts generated successfully:', data);
      
      // Save generated scripts with title ideas
      setGeneratedScripts({
        scriptA: data.scriptA,
        scriptB: data.scriptB,
        titleIdeas: data.titleIdeas || [],
        usage: data.usage
      });
      
      // Update formData with final payload
      setFormData({ ...formData, ...finalPayload });
      
      // Move to Step 4
      setCurrentStep(4);
      
    } catch (error) {
      console.error('❌ Error generating scripts:', error);
      alert(`Failed to generate scripts: ${error.message}`);
    } finally {
      setIsGeneratingScripts(false);
    }
  };
  
  const handleSelectScript = (scriptId, variant) => {
    console.log('✅ Script selected:', scriptId, variant);
    
    // Navigate to script editor
    navigate(`/generate/youtube-script/${scriptId}`);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="youtube-storyteller-container">
      {/* Progress Bar */}
      <div className="yt-progress-bar">
        <div className="yt-progress-steps">
          <div className={`yt-progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="yt-step-number">1</div>
            <div className="yt-step-label">Define Content</div>
          </div>
          <div className="yt-progress-line"></div>
          <div className={`yt-progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="yt-step-number">2</div>
            <div className="yt-step-label">Select Blocks</div>
          </div>
          <div className="yt-progress-line"></div>
          <div className={`yt-progress-step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
            <div className="yt-step-number">3</div>
            <div className="yt-step-label">Configure</div>
          </div>
          <div className="yt-progress-line"></div>
          <div className={`yt-progress-step ${currentStep >= 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
            <div className="yt-step-number">4</div>
            <div className="yt-step-label">Choose & Save</div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="yt-step-content">
        {currentStep === 1 && (
          <YoutubeStorytellerStep1 
            formData={formData}
            onComplete={handleStep1Complete}
          />
        )}
        {currentStep === 2 && (
          <YoutubeStorytellerStep2
            formData={formData}
            onBack={handleBack}
            onComplete={handleStep2Complete}
          />
        )}
        {currentStep === 3 && !isGeneratingScripts && (
          <YoutubeStorytellerStep3
            formData={formData}
            onBack={handleBack}
            onComplete={handleStep3Complete}
          />
        )}
        {currentStep === 3 && isGeneratingScripts && (
          <>
            <div className="yt-loading-state-backdrop"></div>
            <div className="yt-loading-state">
              <div className="yt-loading-spinner"></div>
              <h2 className="yt-loading-title">🤖 Generating Your Scripts...</h2>
              <p className="yt-loading-desc">
                AI is crafting 2 unique script variations for you. This usually takes 30-60 seconds.
              </p>
              <div className="yt-loading-steps">
                <div className="yt-loading-step">
                  <i className="fas fa-check-circle"></i>
                  Analyzing your selected content blocks
                </div>
                <div className="yt-loading-step">
                  <i className="fas fa-check-circle"></i>
                  Building narrative structure
                </div>
                <div className="yt-loading-step active">
                  <i className="fas fa-spinner fa-spin"></i>
                  Generating Script A...
                </div>
                <div className="yt-loading-step">
                  <i className="far fa-circle"></i>
                  Generating Script B...
                </div>
              </div>
            </div>
          </>
        )}
        {currentStep === 4 && (
          <YoutubeStorytellerStep4
            formData={formData}
            scriptA={generatedScripts.scriptA}
            scriptB={generatedScripts.scriptB}
            titleIdeas={generatedScripts.titleIdeas}
            usage={generatedScripts.usage}
            onBack={handleBack}
            onSelectScript={handleSelectScript}
          />
        )}
      </div>
    </div>
  );
};

export default YoutubeStoryteller;

